import type ArrayProxy from '@ember/array/proxy';
import { setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { didCancel } from 'ember-concurrency';
import type LocationModel from 'garaje/models/location';
import type LocationSubscriptionModel from 'garaje/models/location-subscription';
import type PrinterModel from 'garaje/models/printer';
import type SubscriptionModel from 'garaje/models/subscription';
import type UserModel from 'garaje/models/user';
import type StateService from 'garaje/services/state';
import type { RecordArray } from 'garaje/utils/type-utils';
import { hash } from 'rsvp';

import type VisitorsEntriesController from '../controller';
import type { VisitorsEntriesRouteModel } from '../route';

import type VisitorsEntriesIndexController from './controller';

export interface VisitorsEntriesIndexRouteModel {
  blocklistContacts: ArrayProxy<UserModel>;
  currentLocation: LocationModel;
  entries: VisitorsEntriesRouteModel;
  idScanContacts: ArrayProxy<UserModel>;
  printers: RecordArray<PrinterModel> | PrinterModel[] | undefined;
  vrSubscription: LocationSubscriptionModel | SubscriptionModel | null;
}

class VisitorsEntriesIndexRoute extends Route {
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  titleToken = '';

  model(): Promise<VisitorsEntriesIndexRouteModel> {
    const { vrSubscription } = this.state;
    const currentLocation = this.state.currentLocation;
    const blocklistContacts = currentLocation.blocklistContacts;
    const idScanContacts = currentLocation.idScanContacts;
    const entries = <VisitorsEntriesRouteModel>this.modelFor('visitors.entries');
    const { printers } = entries;

    return hash({
      blocklistContacts,
      currentLocation,
      entries,
      idScanContacts,
      printers,
      vrSubscription,
    });
  }

  afterModel(model: VisitorsEntriesIndexRouteModel): void | Promise<unknown> {
    const entries = model.entries;
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = <VisitorsEntriesController>this.controllerFor('visitors.entries');

    const props = {
      refresh: '',
      page: 0,
      limit: 25,
      totalLoadedEntries: 0,
      date: entries.date,
      query: entries.query,
      filter: entries.filter,
      sort: entries.sort,
      selectedFlow: entries.selectedFlow ?? '',
    };

    setProperties(controller, props);

    void controller.loadExecutives.perform();
    // Cancel any load that's still resolving from a prior refresh. Without this,
    // `loadEntries` (a dropTask) silently drops the new perform() while the
    // in-flight call finishes with the previous params — so rapid filter changes
    // (e.g. toggling 2-3 visitor types in succession while a search query is set)
    // leave the table reflecting the first click instead of the last. (SQ-7395)
    void controller.loadEntries.cancelAll();
    return controller.loadEntries.perform().catch((e) => {
      if (!didCancel(e)) {
        throw e;
      }
      return this.router.transitionTo('visitors.entries');
    });
  }

  setupController(
    controller: VisitorsEntriesIndexController,
    model: VisitorsEntriesIndexRouteModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    void controller.refreshLocationsCapacity();
  }
}

export default VisitorsEntriesIndexRoute;
